import * as _ from "./utils.js";
import { objectTypes, guessObjectType, usedObjectTypes } from "./object-types.js";
import ConceptScheme from "./concept-scheme.js";
import languagePreference from "./language-preference.js";
import mappingTypes from "./mapping-types.js";
const isUpperCase = (str) => {
  return /^[A-Z]*$/.test(str);
};
const getNested = (object, path) => {
  return path.split(".").reduce(
    (xs, x) => xs && xs[x] ? xs[x] : null,
    object
  );
};
const addContext = (jskos) => {
  let array = jskos instanceof Array ? jskos : [jskos];
  array.forEach((resource) => {
    resource["@context"] = "https://gbv.github.io/jskos/context.json";
  });
  return jskos;
};
const clean = (jskos) => {
  Object.keys(jskos).forEach((key) => {
    if (isUpperCase(key) || key.startsWith("_")) {
      delete jskos[key];
    } else {
      if (jskos[key] != null && typeof jskos[key] === "object") {
        jskos[key] = clean(jskos[key]);
      }
    }
  });
  return jskos;
};
const cleanJSKOS = clean;
const copyDeep = (object, replaceCircular = [], skipUnderscore = true) => {
  replaceCircular = replaceCircular.concat([
    "ancestors",
    "narrower",
    "broader",
    "mappings",
    "memberList",
    "TOPCONCEPTS",
    "MAPPINGS",
    "PROVIDER"
  ]);
  let clone = Array.isArray(object) ? [] : {};
  for (let i in object) {
    if (skipUnderscore && i[0] == "_") {
      continue;
    }
    if (replaceCircular.includes(i)) {
      if (object[i] && Array.isArray(object[i])) {
        if (object[i].length > 0) {
          clone[i] = [null];
        } else {
          clone[i] = [];
        }
        continue;
      } else {
        clone[i] = null;
        continue;
      }
    }
    if (i == "inScheme") {
      let inScheme = [];
      for (let scheme of object.inScheme) {
        let newScheme = { uri: scheme.uri };
        if (scheme.notation) {
          newScheme.notation = scheme.notation;
        }
        if (scheme.prefLabel) {
          newScheme.prefLabel = scheme.prefLabel;
        }
        inScheme.push(newScheme);
      }
      clone.inScheme = inScheme;
      continue;
    }
    if (object[i] != null && typeof object[i] == "object") {
      clone[i] = copyDeep(object[i]);
    } else {
      clone[i] = object[i];
    }
  }
  return clone;
};
const deepCopy = copyDeep;
const getAllUris = (object) => {
  if (!object) {
    return [];
  }
  return (object.uri ? [object.uri] : []).concat(object.identifier || []).filter(Boolean);
};
const compare = (object1, object2) => {
  if (object1 == null && object2 == null) {
    return true;
  }
  const replaceUri = (uri) => uri.replace("https://", "http://");
  const object1uris = getAllUris(object1).map(replaceUri);
  for (let uri of getAllUris(object2).map(replaceUri)) {
    if (object1uris.indexOf(uri) !== -1) {
      return true;
    }
  }
  return false;
};
const compareObjects = compare;
const compareSchemes = compare;
const compareConcepts = compare;
const isConcept = (object) => {
  return _.get(object, "type", [])?.includes("http://www.w3.org/2004/02/skos/core#Concept") || _.get(object, "inScheme") != null || _.get(object, "topConceptOf") != null;
};
const isScheme = (object) => {
  return _.get(object, "type", [])?.includes("http://www.w3.org/2004/02/skos/core#ConceptScheme");
};
const isContainedIn = (object, objects) => {
  if (!object || !objects) {
    return false;
  }
  for (let o of objects) {
    if (compare(object, o)) {
      return true;
    }
  }
  return false;
};
const isSchemeInList = isContainedIn;
const sortConcepts = (concepts, numerical = false) => {
  return concepts.sort(
    (a, b) => {
      let _a = _.get(a, "notation[0]"), _b = _.get(b, "notation[0]");
      if (_a && _b) {
        _a = _a.toLowerCase();
        _b = _b.toLowerCase();
      }
      if (numerical && _a && _b) {
        let __a, __b;
        __a = parseFloat(_a);
        __b = parseFloat(_b);
        if (!__a && !__b) {
          const _a_split = `${_a}`.split(/[^\w\d]/);
          const _b_split = `${_b}`.split(/[^\w\d]/);
          for (let i = 0; i < Math.min(_a_split.length, _b_split.length); i += 1) {
            if (_a_split[i] !== _b_split[i]) {
              __a = parseFloat(_a_split[i]) || _a_split[i];
              __b = parseFloat(_b_split[i]) || _b_split[i];
              break;
            }
          }
          if (!__a && !__b) {
            __a = _a;
            __b = _b;
          }
        }
        _a = __a;
        _b = __b;
      }
      if (!_a || !_b || _a == _b) {
        _a = a.uri;
        _b = b.uri;
      }
      if (_a && _b) {
        if (_a > _b) {
          return 1;
        } else if (_a < _b) {
          return -1;
        }
      }
      return 0;
    }
  );
};
const sortSchemes = (schemes) => {
  let order = ["notation[0]", ["prefLabel.de", "prefLabel.en"], "uri"];
  return schemes.sort(
    (a, b) => {
      for (let path of order) {
        let _a, _b;
        if (Array.isArray(path)) {
          for (let path2 of path) {
            _a = _a || _.get(a, path2);
            _b = _b || _.get(b, path2);
          }
        } else {
          _a = _.get(a, path);
          _b = _.get(b, path);
        }
        if (_a != null && _b != null) {
          _a = _a.toLowerCase();
          _b = _b.toLowerCase();
          if (_a > _b) {
            return 1;
          } else if (_a < _b) {
            return -1;
          }
        }
      }
      return 0;
    }
  );
};
const minifyMapping = (mapping) => {
  let newMapping = _.pick(copyDeep(mapping), ["from", "to", "fromScheme", "toScheme", "creator", "contributor", "type", "created", "modified", "note", "identifier", "uri", "partOf", "mappingRelevance"]);
  for (let fromTo of [newMapping.from, newMapping.to]) {
    _.forOwn(fromTo, (value, key) => {
      let conceptBundle = [];
      for (let concept of value) {
        conceptBundle.push(_.pick(concept, ["uri", "notation"]));
      }
      fromTo[key] = conceptBundle;
    });
  }
  if (newMapping.fromScheme) {
    newMapping.fromScheme = _.pick(newMapping.fromScheme, ["uri", "notation"]);
  }
  if (newMapping.toScheme) {
    newMapping.toScheme = _.pick(newMapping.toScheme, ["uri", "notation"]);
  }
  if (newMapping.partOf) {
    newMapping.partOf = newMapping.partOf.map((c) => _.pick(c, ["uri"]));
  }
  return newMapping;
};
const mappingTypeByUri = function(uri) {
  for (let mappingType of mappingTypes) {
    if (uri == mappingType.uri) {
      return mappingType;
    }
  }
  return null;
};
const defaultMappingType = mappingTypeByUri("http://www.w3.org/2004/02/skos/core#mappingRelation");
const mappingTypeByType = function(type, defaultType = defaultMappingType) {
  let uri;
  if (Array.isArray(type) && type.length > 0) {
    uri = type[0];
  } else {
    uri = type;
  }
  return mappingTypeByUri(uri) || defaultType;
};
const flattenMapping = (mapping, options = {}) => {
  const { language } = options;
  let fromNotation = getNested(mapping, "from.memberSet.0.notation.0");
  let toNotation = getNested(mapping, "to.memberSet.0.notation.0");
  fromNotation = fromNotation !== null ? fromNotation : "";
  toNotation = toNotation !== null ? toNotation : "";
  let type = mappingTypeByUri(getNested(mapping, "type.0"));
  type = type ? type.SHORT : "";
  let fromLabel = prefLabel(getNested(mapping, "from.memberSet.0"), { language, fallbackToUri: false }) || "";
  let toLabel = prefLabel(getNested(mapping, "to.memberSet.0"), { language, fallbackToUri: false }) || "";
  let creator = prefLabel(getNested(mapping, "creator.0"), { language, fallbackToUri: false }) || "";
  return { fromNotation, toNotation, fromLabel, toLabel, type, creator };
};
const csvSerializer = (options = {}) => {
  const delimiter = options.delimiter || ",";
  const quoteChar = options.quoteChar || '"';
  const lineTerminator = options.lineTerminator || "\n";
  const doubleQuote = quoteChar + quoteChar;
  const quote = (s) => quoteChar + (s == null ? "" : s.split(quoteChar).join(doubleQuote)) + quoteChar;
  return (row) => row.map(quote).join(delimiter) + lineTerminator;
};
const conceptsOfMapping = (mapping, side) => {
  let concepts = [];
  for (let s of ["from", "to"]) {
    if (side == null || s === side) {
      concepts = concepts.concat(
        _.get(mapping, `${s}.memberSet`) || _.get(mapping, `${s}.memberChoice`) || _.get(mapping, `${s}.memberList`) || []
      );
    }
  }
  return concepts.filter((c) => c != null);
};
const mappingCSV = (options = {}) => {
  const toCSV = csvSerializer(options);
  const language = options.language || "en";
  if (options.type == null) {
    options.type = true;
  }
  const header = (mappings) => {
    mappings = mappings || [];
    let fields = [];
    for (let side of ["from", "to"]) {
      if (options.schemes) {
        fields.push(`${side}Scheme`);
      }
      let conceptCount = Math.max(...mappings.map((mapping) => conceptsOfMapping(mapping, side).length), 1);
      for (let i = 0; i < conceptCount; i += 1) {
        fields.push(`${side}Notation${i ? i + 1 : ""}`);
        if (options.labels) {
          fields.push(`${side}Label${i ? i + 1 : ""}`);
        }
      }
    }
    if (options.type) {
      fields.push("type");
    }
    if (options.creator) {
      fields.push("creator");
    }
    return toCSV(fields);
  };
  const fromMapping = (mapping, options2 = {}) => {
    let fields = [];
    for (let side of ["from", "to"]) {
      if (options.schemes) {
        fields.push(_.get(mapping, `${side}Scheme.notation[0]`, ""));
      }
      const concepts = conceptsOfMapping(mapping, side);
      let conceptCount = options2[`${side}Count`];
      if (conceptCount == null) {
        conceptCount = concepts.length;
      }
      conceptCount = Math.max(conceptCount, 1);
      for (let i = 0; i < conceptCount; i += 1) {
        fields.push(_.get(concepts[i], "notation[0]", ""));
        if (options.labels) {
          fields.push(prefLabel(concepts[i], { language, fallbackToUri: false }));
        }
      }
    }
    if (options.type) {
      fields.push(_.get(mappingTypeByUri(_.get(mapping, "type[0]")), "SHORT", ""));
    }
    if (options.creator) {
      fields.push(prefLabel(_.get(mapping, "creator[0]"), { language, fallbackToUri: false }));
    }
    return toCSV(fields);
  };
  const fromMappings = (mappings, options2 = { header: true }) => {
    let result = "";
    if (options2.header) {
      result += header(mappings);
    }
    const fromMappingOptions = {
      fromCount: Math.max(...mappings.map((mapping) => conceptsOfMapping(mapping, "from").length)),
      toCount: Math.max(...mappings.map((mapping) => conceptsOfMapping(mapping, "to").length))
    };
    for (let mapping of mappings) {
      result += fromMapping(mapping, fromMappingOptions);
    }
    return result;
  };
  return {
    header,
    fromMapping,
    fromMappings
  };
};
function compareMappingsDeep(mapping1, mapping2) {
  return _.isEqualWith(mapping1, mapping2, (object1, object2, prop) => {
    let mapping12 = { [prop]: object1 };
    let mapping22 = { [prop]: object2 };
    if (prop == "from" || prop == "to") {
      if (!_.isEqualWith(Object.getOwnPropertyNames(_.get(object1, prop, {})), Object.getOwnPropertyNames(_.get(object2, prop, {})))) {
        return false;
      }
      return _.isEqualWith(conceptsOfMapping(mapping12, prop), conceptsOfMapping(mapping22, prop), (concept1, concept2, index) => {
        if (index != void 0) {
          return compare(concept1, concept2);
        }
        return void 0;
      });
    }
    if (prop == "fromScheme" || prop == "toScheme") {
      return compare(object1, object2);
    }
    return void 0;
  });
}
const matchObjectTypes = (a, b) => {
  let aType = guessObjectType(a), bType = guessObjectType(b);
  if (aType && bType && aType != bType) {
    return false;
  }
  return true;
};
const _nullSort = (array) => {
  if (Array.isArray(array)) {
    array.sort((a, b) => {
      if (a === null) {
        return 1;
      }
      if (b === null) {
        return -1;
      }
      return 0;
    });
  }
};
const mergeUris = (a, b) => {
  if (!a || !b) {
    return a;
  }
  if (Array.isArray(a.identifier) || Array.isArray(b.identifier)) {
    a.identifier = _.union(a.identifier || [], b.identifier || []);
  }
  if (!a.uri && b.uri) {
    a.uri = b.uri;
  }
  if (a.uri && b.uri && a.uri != b.uri && !(a.identifier || []).includes(b.uri)) {
    a.identifier = (a.identifier || []).concat([b.uri]);
  }
  if (Array.isArray(a.identifier) && a.uri) {
    a.identifier = a.identifier.filter((uri) => uri !== a.uri);
  }
  if ((a.identifier || []).length == 0) {
    delete a.identifier;
  }
  _nullSort(a.identifier);
  return a;
};
const merge = (a, b, options) => {
  if (!a || !b) {
    return a ? a : b;
  }
  options = options || {};
  let path = options._path || "";
  let detectMismatch = options.detectMismatch || [];
  let skipPaths = options.skipPaths || [];
  let result = {};
  _.forOwn(a, (value, key) => {
    if (skipPaths.includes(path + key)) {
      return;
    }
    if (Array.isArray(value) && Array.isArray(b[key])) {
      result[key] = _.unionWith(value, b[key], (first, second) => {
        if (_.isObject(first) && _.isObject(second)) {
          return compare(first, second);
        }
        return first === second;
      });
      _nullSort(result[key]);
    } else if (_.isObject(value) && _.isObject(b[key])) {
      result[key] = merge(value, b[key], Object.assign({ _path: `${path}${key}.` }, options));
    } else {
      if (value && b[key] && detectMismatch.includes(path + key) && !_.isEqualWith(value, b[key])) {
        throw new Error("Property mismatch in " + path + key);
      }
      result[key] = value;
    }
  });
  _.forOwn(b, (value, key) => {
    if (!a[key]) {
      result[key] = value;
    }
  });
  if (path == "") {
    _.omitMod(result, skipPaths);
  }
  if (options.mergeUris) {
    mergeUris(result, b);
  }
  return result;
};
const normalize = (data) => {
  if (Array.isArray(data)) {
    return data.map((element) => normalize(element));
  } else if (_.isObject(data)) {
    _.forOwn(data, (value, key) => {
      data[key] = normalize(value);
    });
    return data;
  } else {
    if (_.isString(data)) {
      return data.normalize();
    } else {
      return data;
    }
  }
};
const isValidUri = (uri) => {
  const re_js_rfc3986_URI = /^[A-Za-z][A-Za-z0-9+\-.]*:(?:\/\/(?:(?:[A-Za-z0-9\-._~!$&'()*+,;=:]|%[0-9A-Fa-f]{2})*@)?(?:\[(?:(?:(?:(?:[0-9A-Fa-f]{1,4}:){6}|::(?:[0-9A-Fa-f]{1,4}:){5}|(?:[0-9A-Fa-f]{1,4})?::(?:[0-9A-Fa-f]{1,4}:){4}|(?:(?:[0-9A-Fa-f]{1,4}:){0,1}[0-9A-Fa-f]{1,4})?::(?:[0-9A-Fa-f]{1,4}:){3}|(?:(?:[0-9A-Fa-f]{1,4}:){0,2}[0-9A-Fa-f]{1,4})?::(?:[0-9A-Fa-f]{1,4}:){2}|(?:(?:[0-9A-Fa-f]{1,4}:){0,3}[0-9A-Fa-f]{1,4})?::[0-9A-Fa-f]{1,4}:|(?:(?:[0-9A-Fa-f]{1,4}:){0,4}[0-9A-Fa-f]{1,4})?::)(?:[0-9A-Fa-f]{1,4}:[0-9A-Fa-f]{1,4}|(?:(?:25[0-5]|2[0-4][0-9]|[01]?[0-9][0-9]?)\.){3}(?:25[0-5]|2[0-4][0-9]|[01]?[0-9][0-9]?))|(?:(?:[0-9A-Fa-f]{1,4}:){0,5}[0-9A-Fa-f]{1,4})?::[0-9A-Fa-f]{1,4}|(?:(?:[0-9A-Fa-f]{1,4}:){0,6}[0-9A-Fa-f]{1,4})?::)|[Vv][0-9A-Fa-f]+\.[A-Za-z0-9\-._~!$&'()*+,;=:]+)\]|(?:(?:25[0-5]|2[0-4][0-9]|[01]?[0-9][0-9]?)\.){3}(?:25[0-5]|2[0-4][0-9]|[01]?[0-9][0-9]?)|(?:[A-Za-z0-9\-._~!$&'()*+,;=]|%[0-9A-Fa-f]{2})*)(?::[0-9]*)?(?:\/(?:[A-Za-z0-9\-._~!$&'()*+,;=:@]|%[0-9A-Fa-f]{2})*)*|\/(?:(?:[A-Za-z0-9\-._~!$&'()*+,;=:@]|%[0-9A-Fa-f]{2})+(?:\/(?:[A-Za-z0-9\-._~!$&'()*+,;=:@]|%[0-9A-Fa-f]{2})*)*)?|(?:[A-Za-z0-9\-._~!$&'()*+,;=:@]|%[0-9A-Fa-f]{2})+(?:\/(?:[A-Za-z0-9\-._~!$&'()*+,;=:@]|%[0-9A-Fa-f]{2})*)*|)(?:\?(?:[A-Za-z0-9\-._~!$&'()*+,;=:@/?]|%[0-9A-Fa-f]{2})*)?(?:#(?:[A-Za-z0-9\-._~!$&'()*+,;=:@/?]|%[0-9A-Fa-f]{2})*)?$/;
  return uri.match(re_js_rfc3986_URI) !== null;
};
const compareFunctions = {
  /**
   * Compare mappings by their first concept.
   *
   * @param {*} mapping1 - first mapping
   * @param {*} mapping2 - second mapping
   * @param {*} fromTo - side, either `from` or `to`
   */
  mappingsByConcepts: (mapping1, mapping2, fromTo) => {
    let bundleFields = ["memberSet", "memberList", "memberChoice"], notation1, notation2;
    for (let field of bundleFields) {
      notation1 = notation1 || _.get(mapping1, fromTo + "." + field + "[0].notation[0]");
      notation2 = notation2 || _.get(mapping2, fromTo + "." + field + "[0].notation[0]");
    }
    if (notation1 == null || notation1 < notation2) {
      return -1;
    }
    if (notation2 == null || notation1 > notation2) {
      return 1;
    }
    return 0;
  }
};
const userOwnsMapping = (user, mapping) => {
  if (!user || !mapping) {
    return false;
  }
  return [user.uri].concat(Object.values(user.identities || {}).map((identity) => identity.uri)).filter((uri) => uri != null).includes(_.get(mapping, "creator[0].uri"));
};
function notation(item, type) {
  let notation2;
  if (item && item.notation && item.notation.length) {
    notation2 = item.notation[0];
    if (isScheme(item) || type == "scheme") {
      notation2 = notation2.toUpperCase();
    }
  } else if (item && item.inScheme && item.inScheme[0] && item.uri) {
    try {
      const scheme = new ConceptScheme(item && item.inScheme && item.inScheme[0]);
      notation2 = scheme.notationFromUri(item.uri);
    } catch (error) {
    }
  }
  return notation2 || "";
}
function languageMapContent(item, prop, { language } = {}) {
  let languageMap = item && prop ? item[prop] : item;
  if (languageMap) {
    if (languageMap[language]) {
      return languageMap[language];
    }
    language = languagePreference.selectLanguage(languageMap);
    if (language) {
      return languageMap[language];
    }
  }
  return null;
}
function prefLabel(item, options = {}) {
  options = options || {};
  const fallbackToUri = options.fallbackToUri == null ? true : options.fallbackToUri;
  return _.get(item, `prefLabel.${options.language}`) || languageMapContent(item, "prefLabel", options) || (fallbackToUri && item && item.uri ? item.uri : "");
}
function definition(item, options = {}) {
  options = options || {};
  let content = _.get(item, `definition.${options.language}`) || languageMapContent(item, "definition", options) || [];
  if (_.isString(content)) {
    content = [content];
  }
  return content;
}
function mappingRegistryIsStored(registry) {
  return _.get(registry, "stored", _.get(registry, "constructor.stored", _.get(registry, "provider.constructor.stored", false)));
}
function annotationCreatorUri(annotation) {
  if (_.isString(annotation.creator)) {
    return annotation.creator;
  }
  return annotation.creator && annotation.creator.id;
}
function annotationCreatorName(annotation) {
  return _.get(annotation, "creator.name") || "";
}
function annotationCreatorMatches(annotation, uris) {
  return !!(annotation && _.isString(annotation.creator) ? uris && uris.includes(annotation.creator) : uris && annotation.creator && uris.includes(annotation.creator.id));
}
function guessSchemeFromNotation(notation2, schemes) {
  return schemes.filter(({ notationPattern }) => {
    if ((notationPattern || ".+") === ".+") {
      return false;
    }
    return RegExp("^(" + notationPattern + ")$").test(notation2);
  });
}
export {
  addContext,
  annotationCreatorMatches,
  annotationCreatorName,
  annotationCreatorUri,
  clean,
  cleanJSKOS,
  compare,
  compareConcepts,
  compareFunctions,
  compareMappingsDeep,
  compareObjects,
  compareSchemes,
  conceptsOfMapping,
  copyDeep,
  deepCopy,
  defaultMappingType,
  definition,
  flattenMapping,
  getAllUris,
  guessObjectType,
  guessSchemeFromNotation,
  isConcept,
  isContainedIn,
  isScheme,
  isSchemeInList,
  isValidUri,
  languageMapContent,
  mappingCSV,
  mappingRegistryIsStored,
  mappingTypeByType,
  mappingTypeByUri,
  mappingTypes,
  matchObjectTypes,
  merge,
  mergeUris,
  minifyMapping,
  normalize,
  notation,
  objectTypes,
  prefLabel,
  sortConcepts,
  sortSchemes,
  usedObjectTypes,
  userOwnsMapping
};
